import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import useFiltersState from '../reporting_utils/useFiltersState';
import ErrorBoundary from '../reporting_utils/ErrorBoundary';
import RefreshButton from '../reporting_utils/RefreshButton';
import DateRange from '../reporting_utils/DateRange';
import CurrentDomains from '../reporting_utils/CurrentDomains';
import ReportFilters from '../reporting_utils/ReportFilters';
import EveryLeadCount from './EveryLeadCount';
import EmployeePerformanceTable from './EmployeePerformanceTable';
import ConversionSummaryTable from './ConversionSummaryTable';
import { Common } from '../../utils/common';
import EveryLeadAreaChart from './EveryLeadAreaChart';
import CallCenterAreaChart from './CallCenterAreaChart';
import { getFilterValues } from '../reporting_utils/misc';
import FullScreenButton from './FullScreenButton';
import moment from 'moment';
import useDomainSettings from '../reporting_utils/useDomainSettings';
import EmptyReport from '../reporting_utils/EmptyReport';
import AppliedFilters from '../reporting_utils/AppliedFilters';

const pageLoadingFrame = window.pageLoadingFrame;

const defaultFilterState = { tag_ids: [] };

function Dashboard({ domainId }) {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [visitorsCount, setVisitorsCount] = useState({ visitor_count: 0, visitor_count_pre: 0 });
  const [data, setData] = useState({
    chats_count: {
      countofchats: 0,
      countofchatspre: 0,
    },
    texts_count: {
      countoftexts: 0,
      countoftextspre: 0,
    },
    leads_count: {
      countofleads: 0,
      countofleadspre: 0,
    },
    call_counts: {
      countofcalls: 0,
      countofprospects: 0,
      countofalert: 0,
      countofmissedops: 0,
      countofsalesapt: 0,
      outboundcountofcalls: 0,
      countofcallspre: 0,
      countofprospectspre: 0,
      countofalertpre: 0,
      countofmissedopspre: 0,
      countofsalesaptpre: 0,
      outboundcountofcallspre: 0,
    },
    chart_data: [],
    employee_performance: [],
    conversion_breakdown: [],
  });
  const [filters, setFilters] = useFiltersState(defaultFilterState, {}, domainId);
  const [sidebarIsOpen, setSidebarIsOpen] = useState(false);
  const [controlledDateRange, setControlledDateRange] = useState({
    start: moment(filters.start_date, 'YYYYMMDD'),
    end: moment(filters.end_date, 'YYYYMMDD'),
  });

  const { isAutomotiveIndustry, isTextingEnabled, industryType } = useDomainSettings(
    filters.domains,
    filters.show_inactive
  );

  const fetchVisitorsCount = useCallback(async () => {
    if (!filters.domains) {
      return;
    }
    try {
      const res = await fetch('/dashboard_visitors_count', {
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content,
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(getFilterValues(filters)),
      });

      const { visitors_count } = await res.json();
      setVisitorsCount(visitors_count);
    } catch (error) {
      console.error(error);
    }
  }, [filters]);

  const fetchData = useCallback(async () => {
    if (!filters.domains) {
      return;
    }

    pageLoadingFrame('show');

    try {
      const res = await fetch('/dashboard_data', {
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content,
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(getFilterValues(filters)),
      });

      const {
        chats_count,
        texts_count,
        leads_count,
        call_counts,
        chart_data,
        employee_performance,
        conversion_breakdown,
      } = await res.json();

      setData({
        chats_count,
        texts_count,
        leads_count,
        call_counts,
        chart_data,
        employee_performance,
        conversion_breakdown,
      });
    } catch (error) {
      console.error(error);
      Common.notify('Failed to fetch report data', 'error');
    }

    pageLoadingFrame('hide');
  }, [filters]);

  useEffect(() => {
    fetchData();
    fetchVisitorsCount();
  }, [fetchData, fetchVisitorsCount]);

  return (
    <div className={`report-details-container ${isFullScreen ? 'panel-fullscreen-wrap' : ''}`}>
      <div className='report-details-header'>
        <div className='report-details-title'>
          <h1>Dashboard</h1>
        </div>
        <div className='panel-header'>
          <ErrorBoundary>
            <FullScreenButton setIsFullScreen={setIsFullScreen} isFullScreen={isFullScreen} />
            <RefreshButton dataCallbacks={[fetchVisitorsCount, fetchData]} />
            {!sidebarIsOpen && (
              <DateRange
                controlledDateRange={controlledDateRange}
                setControlledDateRange={setControlledDateRange}
                filters={filters}
                setFilters={setFilters}
              />
            )}
          </ErrorBoundary>
        </div>
      </div>
      <ReportFilters
        filters={filters}
        defaultFilterState={defaultFilterState}
        open={sidebarIsOpen}
        setOpen={setSidebarIsOpen}
        setFilters={setFilters}
        controlledDateRange={controlledDateRange}
        setControlledDateRange={setControlledDateRange}
      />
      {filters.domains ? (
        <div>
          <ErrorBoundary>
            <div className='applied-filters-container'>
              <div style={{ marginBottom: '20px' }}>
                <button
                  className='btn btn-primary'
                  onClick={() => setSidebarIsOpen(!sidebarIsOpen)}
                >
                  <span className='fa fa-plus' /> Add Filters
                </button>
              </div>
              {!Common.in_crm_mode() ? (
                <CurrentDomains filters={filters} setFilters={setFilters} />
              ) : null}
              <ErrorBoundary>
                <AppliedFilters
                  defaultFilterState={defaultFilterState}
                  filters={filters}
                  setFilters={setFilters}
                />
              </ErrorBoundary>
            </div>
          </ErrorBoundary>
          <div className='row'>
            {industryType !== 'Call Center' ? (
              <div className='col-md-3'>
                <EveryLeadCount
                  count={visitorsCount.visitor_count}
                  prevCount={visitorsCount.visitor_count_pre}
                  title='Visitors'
                  icon='fa-mouse-pointer'
                  colorClassName='visitors'
                />
              </div>
            ) : null}
            <div className='col-md-3'>
              <EveryLeadCount
                href={Common.in_crm_mode() ? undefined : 'filtered_call_details/all/all/all/all'}
                count={data.call_counts.countofcalls}
                prevCount={data.call_counts.countofcallspre}
                title='Inbound Calls'
                icon='fa-phone'
                colorClassName='call'
              />
            </div>
            {industryType == 'Call Center' ? (
              <div className='col-md-3'>
                <EveryLeadCount
                  count={data.call_counts.outboundcountofcalls}
                  prevCount={data.call_counts.outboundcountofcallspre}
                  title='Outbound Calls'
                  icon='fa-phone'
                  colorClassName='outboundcalls'
                />
              </div>
            ) : null}
            <div className='col-md-2'>
              <EveryLeadCount
                href={
                  Common.in_crm_mode() ? undefined : 'filtered_call_details/all/prospects/all/all'
                }
                count={data.call_counts.countofprospects}
                prevCount={data.call_counts.countofprospectspre}
                title='Prospects'
                icon='fa-check-square'
                colorClassName='prospects'
              />
            </div>
            <div className='col-md-2'>
              <EveryLeadCount
                href={
                  Common.in_crm_mode()
                    ? undefined
                    : 'filtered_call_details/all/all/Appointments/all'
                }
                count={data.call_counts.countofsalesapt}
                prevCount={data.call_counts.countofsalesaptpre}
                title={isAutomotiveIndustry ? 'Appointments' : 'Booked Calls'}
                icon='fa-calendar'
                colorClassName='appointments'
              />
            </div>
            {industryType !== 'Call Center' ? (
              <div className='col-md-2'>
                <EveryLeadCount
                  href={
                    Common.in_crm_mode() ? undefined : 'filtered_call_details/all/all/all/Alert'
                  }
                  count={data.call_counts.countofalert}
                  prevCount={data.call_counts.countofalertpre}
                  title={isAutomotiveIndustry ? 'Alerts' : 'Missed Opps'}
                  icon='fa-warning'
                  colorClassName='visitors'
                  invertChangeColor={true}
                />
              </div>
            ) : (
              <div className='col-md-2'>
                <EveryLeadCount
                  href={
                    Common.in_crm_mode() ? undefined : 'filtered_call_details/all/all/all/Alert'
                  }
                  count={data.call_counts.missedops}
                  prevCount={data.call_counts.missedopspre}
                  title={isAutomotiveIndustry ? 'Alerts' : 'Missed Opps'}
                  icon='fa-warning'
                  colorClassName='visitors'
                />
              </div>
            )}
          </div>

          {industryType !== 'Call Center' ? (
            <div className='row'>
              <div className='col-md-3'>
                {isTextingEnabled ? (
                  <EveryLeadCount
                    href={Common.in_crm_mode() ? undefined : 'text_details/all'}
                    count={data.texts_count.countoftexts}
                    prevCount={data.texts_count.countoftextspre}
                    title='Texts'
                    icon='fa-comment-o'
                    colorClassName='text'
                  />
                ) : null}
                <EveryLeadCount
                  count={data.call_counts.outboundcountofcalls}
                  prevCount={data.call_counts.outboundcountofcallspre}
                  title='Outbound Calls'
                  icon='fa-phone'
                  colorClassName='outboundcalls'
                />
                <EveryLeadCount
                  href={Common.in_crm_mode() ? undefined : 'chat_details/all'}
                  count={data.chats_count.countofchats}
                  prevCount={data.chats_count.countofchatspre}
                  title='Chats'
                  icon='fa-comments-o'
                  colorClassName='chat'
                />
                <EveryLeadCount
                  href={Common.in_crm_mode() ? undefined : 'lead_details/all'}
                  count={data.leads_count.countofleads}
                  prevCount={data.leads_count.countofleadspre}
                  title='WebForm Submissions'
                  icon='fa-envelope-o'
                  colorClassName='lead'
                />
              </div>
              <div className='col-md-9'>
                {industryType !== 'Call Center' ? (
                  <EmployeePerformanceTable
                    key={isAutomotiveIndustry}
                    isAutomotiveIndustry={isAutomotiveIndustry}
                    tableData={data.employee_performance}
                  />
                ) : (
                  <EveryLeadAreaChart isTextingEnabled={isTextingEnabled} data={data.chart_data} />
                )}
              </div>
            </div>
          ) : (
            <div className='row'>
              <div className='col-md-12'>
                <CallCenterAreaChart isTextingEnabled={isTextingEnabled} data={data.chart_data} />
              </div>
            </div>
          )}

          <ErrorBoundary>
            <div className='row' style={{ marginTop: '10px' }}>
              <div className='col-xs-12'>
                <ConversionSummaryTable
                  tableData={data.conversion_breakdown}
                  isAutomotiveIndustry={isAutomotiveIndustry}
                  isTextingEnabled={isTextingEnabled}
                  industryType={industryType}
                />
              </div>
            </div>
          </ErrorBoundary>
        </div>
      ) : (
        <EmptyReport onClick={() => setSidebarIsOpen(!sidebarIsOpen)} />
      )}
    </div>
  );
}

Dashboard.propTypes = {
  domainId: PropTypes.string,
};

export default Dashboard;
